import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { ErrorMessageInterceptor } from '@capturum/complete';
import { GUEST_DOWNLOAD_URL_FRAGMENT } from '@features/guest/models/transition-document-download.model';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class CustomErrorMessageInterceptor extends ErrorMessageInterceptor {
    // Shared by every interceptor instance (the login module provides its own HttpClient).
    public static redirectedToOfflineLogin = false;

    public catchErrorLogic(err: HttpErrorResponse, request?: HttpRequest<any>): void {
        if (err.url?.includes(GUEST_DOWNLOAD_URL_FRAGMENT)) {
            return;
        }

        if (this.shouldRedirectToOfflineLogin(err, request)) {
            // Redirect once per page load. The login route itself calls the API (translations) before
            // the browser URL changes, so a second redirect would cancel the first and loop forever.
            if (!CustomErrorMessageInterceptor.redirectedToOfflineLogin) {
                CustomErrorMessageInterceptor.redirectedToOfflineLogin = true;
                void this.router.navigate(['/auth/login']);
            }

            return;
        }

        super.catchErrorLogic(err, request);
    }

    // Temporary (PHP2Shell, CVE-2026-109618/-109619): the API is switched off and the login page shows
    // the offline notice. The AWS error carries no CORS headers, so the browser reports status 0
    // instead of the 500 it really is. Remove together with environment.loginOfflineNotice.
    public shouldRedirectToOfflineLogin(err: HttpErrorResponse, request?: HttpRequest<any>): boolean {
        if (!environment.loginOfflineNotice || this.currentPath().startsWith('/auth')) {
            return false;
        }

        const url = err.url ?? request?.url ?? '';
        const isApiCall = url.startsWith(environment.baseUrl);

        return isApiCall && (err.status === HttpStatusCode.InternalServerError || err.status === 0);
    }

    public currentPath(): string {
        return window.location.pathname;
    }

    public exceptionUrls(err: HttpErrorResponse): boolean {
        // The deleted-user lookup fires on every blur of the e-mail field on the create form. A 429
        // or a 403 there is not something the admin asked for, so it must not raise a toast; the
        // component treats any failure as "no deleted user found".
        if (err.url?.endsWith('/user/trashed/lookup')) {
            return false;
        }

        // The login page turns a refused role switch into its own actionable message.
        if (err.url?.endsWith('/user/role/by-key')) {
            return false;
        }

        if (err.status === HttpStatusCode.UnprocessableEntity && err.url?.includes('/transition-document/') && err.url?.endsWith('/answers')) {
            return false;
        }

        if (err.status === HttpStatusCode.Conflict && err.url?.includes('/transition-document/') && err.url?.endsWith('/reopen')) {
            return false;
        }

        return super.exceptionUrls(err);
    }
}
